import React, { useEffect, useState } from 'react';

import {
  adminCancelMultipleBookings,
  adminRequestMultipleBookingsCancellation,
  ConfirmationTypes,
  getTypeOfUser,
  ILongContractRequestsData,
  markMultipleAsPaid,
  pharmacistCancelMultipleBookings,
  pharmacistRequestMultipleBookingsCancellation,
  pharmacyCancelMultipleBookings,
  pharmacyRequestMultipleBookingsCancellation,
  TypeOfUser,
} from '@pharmaplan/common';

import { FormikValues } from 'formik';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import strings from '../../localization';
import useSelfService from '../useSelfService';
import { setDialog } from '../../reducers/dialogReducer';
import DeactivateDialog from '../../components/Admin/PharmacistList/DeactivateDialog';
import { DeactivateModalIcon } from '../../helpers/Constants';
import ValidatorCancellation from '../../components/Admin/PharmacistList/DeactivateDialog/validator';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import useAdmin from '../useAdmin';

const { pharmacist, technician, student, pharmacy, admin, superAdmin } = TypeOfUser;

export enum MultiBookingActionType {
  CancelBooking,
  MarkAsPaid,
}

const useMultiBookingActions = (
  data: Array<ILongContractRequestsData>,
  type: MultiBookingActionType,
) => {
  const dispatch = useAppDispatch();
  const { isSelfService } = useSelfService();

  const userType = useAppSelector(getTypeOfUser);
  const { isAdmin } = useAdmin();

  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);

  const isAllSelected = selectedIds?.length === data?.length;

  const pharmacistRequestCancellation = (values: FormikValues) => {
    dispatch(
      pharmacistRequestMultipleBookingsCancellation({
        bookingIds: selectedIds,
        reason: values?.reason,
      }),
    );
  };

  const pharmacyRequestCancellation = (values: FormikValues) => {
    dispatch(
      pharmacyRequestMultipleBookingsCancellation({
        bookingIds: selectedIds,
        reason: values?.reason,
      }),
    );
  };

  const adminRequestCancellation = (values: FormikValues) => {
    dispatch(
      adminRequestMultipleBookingsCancellation({
        bookingIds: selectedIds,
        reason: values?.reason,
      }),
    );
  };

  const pharmacistCancellation = () => {
    dispatch(pharmacistCancelMultipleBookings({ bookingIds: selectedIds }));
  };

  const pharmacyCancellation = () => {
    dispatch(pharmacyCancelMultipleBookings({ bookingIds: selectedIds }));
  };

  const adminCancellation = () => {
    dispatch(adminCancelMultipleBookings({ bookingIds: selectedIds }));
  };

  const markMultiplePaid = () => {
    const paid = data.map((item) =>
      ({
        bookingId: item.bookingId ?? '',
        paid: selectedIds.includes(item.bookingId ?? ''),
      }));

    dispatch(markMultipleAsPaid(paid));
  };

  const handleCheckboxClick = (id: string) =>
    () => {
      if (selectedIds?.includes(id)) {
        setSelectedIds(selectedIds.filter((item: string) =>
          item !== id));
      } else {
        setSelectedIds([...selectedIds, id]);
      }
    };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(() =>
        data.map((item) =>
          item.bookingId ?? ''));
    }
  };

  // FOR FULL-SERVICE CANCELLATION CAN ONLY BE REQUESTED
  const requestCancellationConfig = {
    [pharmacist]: pharmacistRequestCancellation,
    [technician]: pharmacistRequestCancellation,
    [student]: pharmacistRequestCancellation,
    [pharmacy]: pharmacyRequestCancellation,
    [admin]: adminRequestCancellation,
    [superAdmin]: adminRequestCancellation,
  };

  const cancelBookingConfig = {
    [pharmacist]: pharmacistCancellation,
    [technician]: pharmacistCancellation,
    [student]: pharmacistCancellation,
    [pharmacy]: pharmacyCancellation,
    [admin]: adminCancellation,
    [superAdmin]: adminCancellation,
  };

  const confirmRequestCancellation = () => {
    dispatch(
      setDialog({
        Component: (
          <DeactivateDialog
            onSubmit={
              requestCancellationConfig[
                userType as keyof typeof requestCancellationConfig
              ]
            }
            confirmationMessage={
              strings.formatString(
                strings.areYouSureCancelSelected,
                selectedIds.length,
              ) as string
            }
            id=""
            initialValues={{
              bookingIds: selectedIds,
              reason: '',
            }}
            heading={strings.requestCancellation}
            iconType={DeactivateModalIcon.warning}
            validator={ValidatorCancellation()}
            name="reason"
            // customComponent={<CancellationDateTime date={dateTime} />}
          />
        ),
        heading: {
          title: '',
        },
        showCloseButton: true,
      }),
    );
  };

  const confirmSelfCancel = () => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={isAdmin ? ConfirmationTypes.booking : ConfirmationTypes.cancelTAndCModal}
            confirmAction={cancelBookingConfig[userType as keyof typeof cancelBookingConfig]}
          />
        ),
        showCloseButton: false,
      }),
    );
  };
  const submitActions = {
    [MultiBookingActionType.CancelBooking]: isSelfService
      ? confirmSelfCancel
      : confirmRequestCancellation,
    [MultiBookingActionType.MarkAsPaid]: markMultiplePaid,
  };

  const labelConfig = {
    [MultiBookingActionType.CancelBooking]: isSelfService
      ? strings.cancelBooking
      : strings.requestCancellation,
    [MultiBookingActionType.MarkAsPaid]: strings.markAsPaid,
  };

  useEffect(() => {
    if (type === MultiBookingActionType.MarkAsPaid) {
      const allPaidBookings = data
        ?.filter((item) =>
          item.markAsPaid)
        .map((item) =>
          item.bookingId ?? '');

      setSelectedIds(allPaidBookings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    handleCheckboxClick,
    handleSelectAll,
    isAllSelected,
    selectedIds,
    handleSubmit: submitActions[type],
    label: labelConfig[type],
  };
};

export default useMultiBookingActions;
